/**
 * 画面の切り替え
 *
 * ルータは入れない。画面が5つしかない段階でルータを入れると、
 * 依存だけ増えて得るものがない。URL のハッシュだけ見る。
 * 共有された URL がその画面を開く、という一点だけは満たしておく。
 */

import { Suspense, lazy, useEffect, useState } from 'react';
import { sound } from './sound/engine';
import { loadSound } from './settings';
import type { SceneName } from './sound/music';
import './app.css';

/*
 * 画面ごとに切り分ける。
 *
 * まとめて配ると 954kB（gzip 311kB）になり、スマホの初回読み込みに響く。
 * 大半は史実の散文で、**開いた画面が要らない分まで一緒に落ちてくる**。
 * 動的 import にすると、その画面を開いたときに初めて落ちる。
 *
 * ポーカーは `@tenka/data` を一切使わないので、この分け方の恩恵が
 * いちばん大きい（史実の散文をまったく落とさずに遊べる）。
 */
const BoardScreen = lazy(() => import('./board/BoardScreen').then((m) => ({ default: m.BoardScreen })));
const BattleScreen = lazy(() => import('./battle/BattleScreen').then((m) => ({ default: m.BattleScreen })));
const HoldemScreen = lazy(() => import('./holdem/HoldemScreen').then((m) => ({ default: m.HoldemScreen })));
const RoomScreen = lazy(() => import('./online/RoomScreen').then((m) => ({ default: m.RoomScreen })));
const WarlordSelect = lazy(() => import('./WarlordSelect').then((m) => ({ default: m.WarlordSelect })));
const CodexScreen = lazy(() => import('./Codex').then((m) => ({ default: m.Codex })));
const MypageScreen = lazy(() => import('./Mypage').then((m) => ({ default: m.Mypage })));
/*
 * 表紙だけは切り分けない。**最初に出る画面**なので、
 * 動的 import にすると「読み込んでいます…」から始まることになる。
 * `@tenka/data` を使わないので、まとめても数 kB しか増えない。
 */
import { Title } from './Title';

type Screen = 'title' | 'board' | 'battle' | 'holdem' | 'room' | 'warlords' | 'codex' | 'mypage';

/** 上の帯に出す画面。表紙は帯に並べず、題字から戻る */
const SCREENS: { id: Screen; label: string }[] = [
  { id: 'board', label: '双六' },
  { id: 'battle', label: '合戦' },
  { id: 'warlords', label: '武将' },
  { id: 'holdem', label: 'ポーカー' },
  { id: 'room', label: '対戦' },
  { id: 'codex', label: '図鑑' },
  { id: 'mypage', label: '控え' },
];

/**
 * 何も指していなければ表紙。
 *
 * 以前は盤面が既定だった。開くといきなり40マスが出て、
 * 何の遊びなのかどこにも書かれていなかった。
 */
/**
 * 画面と曲の対応。
 *
 * 対戦・武将・図鑑・控えには曲を割り当てない。**読む画面と、
 * 人を待つ画面では鳴らさない** — 読んでいるあいだ延々と鳴っているのは、
 * 雰囲気ではなく邪魔である。
 */
const SCENE_OF: Partial<Record<Screen, SceneName>> = {
  title: 'title',
  board: 'board',
  battle: 'battle',
  holdem: 'holdem',
};

const fromHash = (): Screen => {
  const id = location.hash.slice(1);
  return SCREENS.some((s) => s.id === id) ? (id as Screen) : 'title';
};

const go = (id: string): void => {
  location.hash = `#${id}`;
};

export function App(): React.JSX.Element {
  const [screen, setScreen] = useState<Screen>(fromHash);

  useEffect(() => {
    const onHash = (): void => setScreen(fromHash());
    addEventListener('hashchange', onHash);
    return () => removeEventListener('hashchange', onHash);
  }, []);

  /*
   * 最初の指で音を開ける。
   *
   * ブラウザは人が触るまで音を出さない（自動再生の制限）ので、
   * **触る前に鳴らそうとしても黙って失敗する。**
   * `once` を付けて、開いたら外す。
   */
  useEffect(() => {
    const prefs = loadSound();
    sound.setSfx(prefs.sfx);
    sound.setMusic(prefs.music);
    sound.setVolume(prefs.volume);

    const open = (): void => sound.unlock();
    for (const ev of ['pointerdown', 'keydown', 'touchstart']) {
      addEventListener(ev, open, { once: true, passive: true });
    }
    return () => {
      for (const ev of ['pointerdown', 'keydown', 'touchstart']) removeEventListener(ev, open);
    };
  }, []);

  // 画面が変われば曲も変わる（触る前に頼んでも、開いた時点で始まる）
  useEffect(() => {
    sound.scene(SCENE_OF[screen] ?? null);
  }, [screen]);

  if (screen === 'title') {
    return (
      <Title
        onGo={(id) => {
          go(id);
          setScreen(id as Screen);
        }}
      />
    );
  }

  return (
    <>
      <nav className="topnav" aria-label="画面">
        {/* 表紙へ戻る口。無いと、入ったら出られない */}
        <a
          href="#title"
          className="home"
          aria-label="表紙へ戻る"
          onClick={() => setScreen('title')}
        >
          天下双六
        </a>
        {SCREENS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            aria-current={screen === s.id ? 'page' : undefined}
            onClick={() => setScreen(s.id)}
          >
            {s.label}
          </a>
        ))}
      </nav>
      <Suspense fallback={<p className="loading">読み込んでいます…</p>}>
        {screen === 'board' ? (
          <BoardScreen />
        ) : screen === 'battle' ? (
          <BattleScreen />
        ) : screen === 'holdem' ? (
          <HoldemScreen />
        ) : screen === 'room' ? (
          <RoomScreen />
        ) : screen === 'codex' ? (
          <CodexScreen />
        ) : screen === 'mypage' ? (
          <MypageScreen />
        ) : (
          <WarlordSelect />
        )}
      </Suspense>
    </>
  );
}
